/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import merge from 'deepmerge';
import { navigate } from 'gatsby';
import {
  addPersistingQueryParamsToPath,
  getPagePath,
} from '../helpers/urlHelper';

/* globals CSS_CONFIG */

const ensureTrailingSlash = (url: string): string => url.replace(/\/?$/, '/');

const removeSlash = (url: string): string => {
  return url.indexOf('/') === 0 ? url.substring(1) : url;
};

const INTERNAL_SERVER_ERROR_REGEXP = /^503$/;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorCode = `${error?.response?.status}`;
    const reqUrl = `${error?.config?.url}`;
    if (
      errorCode.match(INTERNAL_SERVER_ERROR_REGEXP) ||
      (reqUrl.includes('/greeting') && error?.isAxiosError)
    ) {
      const path = getPagePath('service-unavailable');

      navigate(
        addPersistingQueryParamsToPath(path, {
          exclude: ['vpc', 'projectid', 'imagination', 'event_action'],
        }),
      );
    }
    return Promise.reject(error);
  },
);

class HttpClient {
  private baseUrl: string;

  private middleware: any[] = [];

  private baseConfig: AxiosRequestConfig = {
    timeout: 20000,
    headers: {
      'country-Language': `${CSS_CONFIG.COUNTRY}-${CSS_CONFIG.LANGUAGE}`,
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  };

  public constructor(baseUrl = '') {
    this.baseUrl = ensureTrailingSlash(baseUrl);
  }

  public applyMiddleware = (
    initialConfig: AxiosRequestConfig = {},
  ): AxiosRequestConfig =>
    this.middleware.reduce(
      (config, applyChanges): void => config.then(applyChanges),
      Promise.resolve(merge(this.baseConfig, initialConfig)),
    );

  public get = async <T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.get(finalUrl, finalConfig);
  };

  public post = async <T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.post(finalUrl, body, finalConfig);
  };

  public put = async <T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.put(finalUrl, body, finalConfig);
  };

  public delete = async <T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.delete(finalUrl, finalConfig);
  };
}

export default HttpClient;
